import { PICKUP_STATUS } from '../constants';
import { MOVE_TYPES, STATUS_BUCKETS, StatusBucket, MoveType } from './constants';
import type { GroupCountFields, MoveTypeCountFields, SwapBoardTrailer } from './types';

const COMPLETED_STATUSES = new Set([
  PICKUP_STATUS.completed.toLowerCase(),
  PICKUP_STATUS.noFreight.toLowerCase(),
  PICKUP_STATUS.voided.toLowerCase(),
]);

const ASSIGNED_STATUSES = new Set([PICKUP_STATUS.assigned.toLowerCase()]);

export const getStatusBucket = (status?: string | null): StatusBucket => {
  const normalized = (status || '').toLowerCase();
  if (COMPLETED_STATUSES.has(normalized)) {
    return STATUS_BUCKETS.COMPLETED;
  }
  if (ASSIGNED_STATUSES.has(normalized)) {
    return STATUS_BUCKETS.ASSIGNED;
  }
  return STATUS_BUCKETS.UNASSIGNED;
};

export const normalizeMoveType = (moveType?: string | null): MoveType | null => {
  const normalized = (moveType || '').trim().toLowerCase();
  if (normalized === MOVE_TYPES.SWAP) {
    return MOVE_TYPES.SWAP;
  }
  if (normalized === MOVE_TYPES.DROP) {
    return MOVE_TYPES.DROP;
  }
  if (normalized === MOVE_TYPES.HOOK) {
    return MOVE_TYPES.HOOK;
  }
  return null;
};

const createEmptyMoveTypeCounts = () => ({
  [STATUS_BUCKETS.UNASSIGNED]: 0,
  [STATUS_BUCKETS.ASSIGNED]: 0,
  [STATUS_BUCKETS.COMPLETED]: 0,
  total: 0,
});

export const computeGroupCounts = (trailers: SwapBoardTrailer[] = []): GroupCountFields => {
  const counts = {
    assignedCount: 0,
    unassignedCount: 0,
    completedCount: 0,
  };

  trailers.forEach((trailer) => {
    const bucket = getStatusBucket(trailer.status);
    if (bucket === STATUS_BUCKETS.ASSIGNED) {
      counts.assignedCount += 1;
    } else if (bucket === STATUS_BUCKETS.COMPLETED) {
      counts.completedCount += 1;
    } else {
      counts.unassignedCount += 1;
    }
  });

  return counts;
};

export const computeMoveTypeCounts = (trailers: SwapBoardTrailer[] = []): MoveTypeCountFields => {
  const counts = {
    [MOVE_TYPES.SWAP]: createEmptyMoveTypeCounts(),
    [MOVE_TYPES.DROP]: createEmptyMoveTypeCounts(),
    [MOVE_TYPES.HOOK]: createEmptyMoveTypeCounts(),
  };

  trailers.forEach((trailer) => {
    const moveType = normalizeMoveType(trailer.moveType);
    if (!moveType) {
      return;
    }
    const bucket = getStatusBucket(trailer.status);
    counts[moveType][bucket] += 1;
    counts[moveType].total += 1;
  });

  return {
    swapsUnassigned: counts[MOVE_TYPES.SWAP][STATUS_BUCKETS.UNASSIGNED],
    swapsAssigned: counts[MOVE_TYPES.SWAP][STATUS_BUCKETS.ASSIGNED],
    swapsCompleted: counts[MOVE_TYPES.SWAP][STATUS_BUCKETS.COMPLETED],
    swapsTotal: counts[MOVE_TYPES.SWAP].total,
    dropsUnassigned: counts[MOVE_TYPES.DROP][STATUS_BUCKETS.UNASSIGNED],
    dropsAssigned: counts[MOVE_TYPES.DROP][STATUS_BUCKETS.ASSIGNED],
    dropsCompleted: counts[MOVE_TYPES.DROP][STATUS_BUCKETS.COMPLETED],
    dropsTotal: counts[MOVE_TYPES.DROP].total,
    hooksUnassigned: counts[MOVE_TYPES.HOOK][STATUS_BUCKETS.UNASSIGNED],
    hooksAssigned: counts[MOVE_TYPES.HOOK][STATUS_BUCKETS.ASSIGNED],
    hooksCompleted: counts[MOVE_TYPES.HOOK][STATUS_BUCKETS.COMPLETED],
    hooksTotal: counts[MOVE_TYPES.HOOK].total,
  };
};
